import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Pause, Play, RotateCcw, Square } from "lucide-react";

import { cn } from "lib/utils";
import { defaultRecordingName } from "utils/audio";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import { UploadProgress } from "components/upload-progress";
import { toast } from "utils/toast";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";

const PANEL_HEIGHT = "h-[22rem]";
const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

function useAudioDevices() {
  const [devices, setDevices] = useState([]);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    navigator.mediaDevices?.addEventListener?.("devicechange", refresh);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", refresh);
  }, [refresh]);

  return { devices, refresh };
}

/**
 * Recording state machine: idle -> recording <-> paused -> stopped.
 * Elapsed time is tracked from wall-clock timestamps (not a naive tick
 * counter) so pausing genuinely freezes it rather than drifting.
 */
function useRecorder() {
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const startedAtRef = useRef(null);
  const accumulatedRef = useRef(0);
  const tickRef = useRef(null);

  const [state, setState] = useState("idle"); // idle | recording | paused | stopped
  const [blob, setBlob] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const teardownAudio = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    setAnalyser(null);
  }, []);

  const readElapsed = useCallback(
    () => accumulatedRef.current + (startedAtRef.current === null ? 0 : performance.now() - startedAtRef.current),
    []
  );

  const startTicking = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setElapsedMs(readElapsed()), 200);
  }, [readElapsed]);

  const start = useCallback(
    async (deviceId) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioCtx();
      const node = audioContext.createAnalyser();
      node.fftSize = 1024;
      audioContext.createMediaStreamSource(stream).connect(node);
      audioContextRef.current = audioContext;
      setAnalyser(node);

      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        setBlob(new Blob(chunksRef.current, { type: recorder.mimeType }));
        setState("stopped");
      };
      mediaRecorderRef.current = recorder;
      recorder.start();

      accumulatedRef.current = 0;
      startedAtRef.current = performance.now();
      setElapsedMs(0);
      setState("recording");
      startTicking();
    },
    [startTicking]
  );

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    recorder.pause();
    accumulatedRef.current = readElapsed();
    startedAtRef.current = null;
    setElapsedMs(accumulatedRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    setState("paused");
  }, [readElapsed]);

  const resume = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    recorder.resume();
    startedAtRef.current = performance.now();
    setState("recording");
    startTicking();
  }, [startTicking]);

  const end = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    mediaRecorderRef.current?.stop();
    teardownAudio();
  }, [teardownAudio]);

  const reset = useCallback(() => {
    setBlob(null);
    setElapsedMs(0);
    accumulatedRef.current = 0;
    startedAtRef.current = null;
    setState("idle");
  }, []);

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      teardownAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, blob, analyser, elapsedMs, start, pause, resume, end, reset };
}

/**
 * Samples the analyser's RMS loudness at a fixed cadence into an unbounded
 * array — the FULL history of the take, not a scrolling window. Nothing is
 * appended unless `active`, so pausing genuinely freezes the array (and thus
 * whatever gets drawn from it) rather than continuing to shift.
 */
function useLevelSamples(analyser, active) {
  const [samples, setSamples] = useState([]);
  const bufferRef = useRef(null);

  useEffect(() => {
    if (!active || !analyser) return;
    bufferRef.current = new Uint8Array(analyser.fftSize);
    const id = setInterval(() => {
      const buffer = bufferRef.current;
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (const value of buffer) {
        const centred = (value - 128) / 128;
        sum += centred * centred;
      }
      const level = Math.min(1, Math.sqrt(sum / buffer.length) * 4);
      setSamples((prev) => [...prev, level]);
    }, 100);
    return () => clearInterval(id);
  }, [analyser, active]);

  const clear = useCallback(() => setSamples([]), []);

  return { samples, clear };
}

function formatElapsed(ms) {
  const seconds = Math.floor(Math.max(0, ms) / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function downsampleToBars(samples, slots) {
  const bars = new Array(slots).fill(0);
  if (samples.length === 0) return bars;
  const bucketSize = samples.length / slots;
  for (let i = 0; i < slots; i += 1) {
    const start = Math.floor(i * bucketSize);
    const end = Math.max(start + 1, Math.floor((i + 1) * bucketSize));
    let max = 0;
    for (let j = start; j < end && j < samples.length; j += 1) {
      if (samples[j] > max) max = samples[j];
    }
    bars[i] = max;
  }
  return bars;
}

/**
 * Live level meter drawn straight from the analyser while actively
 * recording — a continuously scrolling window (bars slide in from the right
 * at a steady ~30/sec), not a downsampled overview, so the motion is smooth
 * and doesn't rescale as more audio accumulates. Only mounted during
 * "recording", so it starts fresh (and stops entirely, cancelling its own
 * animation loop) on every pause/resume — no full-take bookkeeping here.
 */
function LiveWaveform({ analyser, className }) {
  const canvasRef = useRef(null);
  const samplesRef = useRef([]);
  const frameRef = useRef(0);
  const lastPushRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const buffer = analyser ? new Uint8Array(analyser.fftSize) : null;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const BAR = 2;
    const GAP = 3;

    const draw = (time) => {
      frameRef.current = requestAnimationFrame(draw);
      const rect = canvas.getBoundingClientRect();
      const slots = Math.max(1, Math.floor(rect.width / (BAR + GAP)));

      if (time - lastPushRef.current > 33) {
        lastPushRef.current = time;
        let level = 0;
        if (analyser && buffer) {
          analyser.getByteTimeDomainData(buffer);
          let sum = 0;
          for (const value of buffer) {
            const centred = (value - 128) / 128;
            sum += centred * centred;
          }
          level = Math.min(1, Math.sqrt(sum / buffer.length) * 4);
        }
        samplesRef.current.push(level);
        if (samplesRef.current.length > slots) {
          samplesRef.current.splice(0, samplesRef.current.length - slots);
        }
      }
      while (samplesRef.current.length < slots) samplesRef.current.unshift(0);

      context.clearRect(0, 0, rect.width, rect.height);
      context.fillStyle = getComputedStyle(canvas).color;
      const middle = rect.height / 2;
      const samples = samplesRef.current;
      for (let index = 0; index < samples.length; index += 1) {
        const height = Math.max(2, samples[index] * (rect.height - 8));
        const x = index * (BAR + GAP);
        context.globalAlpha = samples[index] < 0.02 ? 0.3 : 1;
        context.beginPath();
        context.roundRect(x, middle - height / 2, BAR, height, BAR / 2);
        context.fill();
      }
      context.globalAlpha = 1;
    };

    frameRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [analyser]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}

/**
 * Draws the take's level history downsampled to fit the canvas width — a
 * static overview, not a live meter. Used for idle (flat line), paused
 * (everything recorded so far) and stopped (the full take); in every case
 * it only redraws when `samples` itself changes, which happens exactly once
 * per pause/end transition and never on a timer, so it genuinely never
 * shifts on its own. Once stopped, `onSeek` lets clicking anywhere on the
 * bars jump audio playback to that point, and `playheadFraction` draws a
 * thin moving cursor over the (otherwise unchanged) bars.
 */
function FrozenWaveform({ samples, playheadFraction, onSeek, className }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const BAR = 2;
    const GAP = 3;
    const slots = Math.max(1, Math.floor(rect.width / (BAR + GAP)));
    const bars = downsampleToBars(samples, slots);

    context.clearRect(0, 0, rect.width, rect.height);
    context.fillStyle = getComputedStyle(canvas).color;
    const middle = rect.height / 2;

    for (let index = 0; index < bars.length; index += 1) {
      const height = Math.max(2, bars[index] * (rect.height - 8));
      const x = index * (BAR + GAP);
      context.globalAlpha = bars[index] < 0.02 ? 0.3 : 1;
      context.beginPath();
      context.roundRect(x, middle - height / 2, BAR, height, BAR / 2);
      context.fill();
    }
    context.globalAlpha = 1;

    if (playheadFraction != null) {
      context.fillStyle = "rgb(248, 113, 113)";
      context.fillRect(playheadFraction * rect.width, 0, 1.5, rect.height);
    }
  }, [samples, playheadFraction]);

  return (
    <canvas
      ref={canvasRef}
      className={cn(className, onSeek && "cursor-pointer")}
      aria-hidden="true"
      onClick={onSeek}
    />
  );
}

function RecordPanel({ onSubmit, uploading }) {
  const { language } = useLanguage();
  const tr = translations[language].recordDialog;
  const { devices, refresh } = useAudioDevices();
  const { state, blob, analyser, elapsedMs, start, pause, resume, end, reset } = useRecorder();
  const { samples, clear: clearSamples } = useLevelSamples(analyser, state === "recording");

  const [deviceId, setDeviceId] = useState("");
  const [name, setName] = useState("");
  const [placeholderName, setPlaceholderName] = useState(() => defaultRecordingName(language));
  const [previewUrl, setPreviewUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rateIndex, setRateIndex] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!blob) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = PLAYBACK_RATES[rateIndex];
  }, [rateIndex, previewUrl]);

  async function handleStart() {
    try {
      await start(deviceId || undefined);
      refresh();
    } catch {
      toast.error(tr.micDenied);
    }
  }

  function handleReRecord() {
    setPlaying(false);
    setPlaybackTime(0);
    setDuration(0);
    setRateIndex(0);
    clearSamples();
    reset();
    setName("");
    setPlaceholderName(defaultRecordingName(language));
  }

  function handleSubmit() {
    if (!blob) return;
    onSubmit(blob, "recorded", name.trim() || placeholderName);
  }

  function togglePlayback() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
  }

  function cycleRate() {
    setRateIndex((i) => (i + 1) % PLAYBACK_RATES.length);
  }

  function handleSeek(event) {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    audio.currentTime = fraction * audio.duration;
  }

  const fieldsDisabled = state === "recording" || state === "paused";
  const playheadFraction = state === "stopped" && duration > 0 ? playbackTime / duration : null;

  return (
    <div
      className={cn(
        "flex min-h-[20rem] flex-col gap-4 overflow-hidden rounded-lg border border-dashed border-white/15 px-4 py-4 sm:px-6 sm:py-6",
        "sm:h-[22rem]"
      )}
    >
      <div className="flex flex-col gap-1.5 text-start">
        <Label htmlFor="record-mic" className="text-xs text-muted-foreground">
          {tr.micLabel}
        </Label>
        <select
          id="record-mic"
          value={deviceId}
          onChange={(event) => setDeviceId(event.target.value)}
          disabled={fieldsDisabled}
          className="h-9 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">{tr.systemDefaultMic}</option>
          {devices.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || tr.micFallback(i + 1)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5 text-start">
        <Label htmlFor="record-name" className="text-xs text-muted-foreground">
          {tr.nameLabel}
        </Label>
        <Input
          id="record-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={placeholderName}
          disabled={fieldsDisabled}
        />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        {state === "recording" ? (
          <LiveWaveform analyser={analyser} className="h-12 w-full text-foreground" />
        ) : (
          <FrozenWaveform
            samples={samples}
            playheadFraction={playheadFraction}
            onSeek={state === "stopped" ? handleSeek : undefined}
            className="h-12 w-full text-foreground"
          />
        )}

        {/* Fixed height so the button row below lands at the same y in
            every state, regardless of what this row's content looks like. */}
        <div className="flex h-6 items-center justify-center gap-2">
          {state === "idle" && (
            <p className="font-mono text-sm tabular-nums text-muted-foreground">{tr.tapToStart}</p>
          )}
          {(state === "recording" || state === "paused") && (
            <p className="font-mono text-sm tabular-nums text-muted-foreground">{formatElapsed(elapsedMs)}</p>
          )}
          {state === "stopped" && (
            <>
              <button
                type="button"
                data-slot="button"
                onClick={togglePlayback}
                aria-label={playing ? tr.pause : tr.play}
                className="flex size-5 items-center justify-center text-foreground transition-colors hover:text-muted-foreground"
              >
                {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
              </button>
              <span className="font-mono text-xs tabular-nums text-muted-foreground">
                {formatElapsed(playbackTime * 1000)} / {formatElapsed(duration * 1000)}
              </span>
              <button
                type="button"
                data-slot="button"
                onClick={cycleRate}
                aria-label={tr.playbackSpeed}
                className="font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {PLAYBACK_RATES[rateIndex]}x
              </button>
            </>
          )}
        </div>

        {state === "idle" && (
          <Button type="button" size="sm" variant="outline" onClick={handleStart} disabled={uploading}>
            <Mic className="size-4" />
            {tr.startRecording}
          </Button>
        )}

        {(state === "recording" || state === "paused") && (
          <div className="flex flex-wrap justify-center gap-2">
            {state === "recording" ? (
              <Button type="button" size="sm" variant="outline" onClick={pause}>
                <Pause className="size-4" />
                {tr.pause}
              </Button>
            ) : (
              <Button type="button" size="sm" variant="outline" onClick={resume}>
                <Play className="size-4" />
                {tr.resume}
              </Button>
            )}
            <Button type="button" size="sm" onClick={end}>
              <Square className="size-4" />
              {tr.end}
            </Button>
          </div>
        )}

        {state === "stopped" && (
          <div className="flex flex-wrap justify-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={handleReRecord} disabled={uploading}>
              <RotateCcw className="size-4" />
              {tr.reRecord}
            </Button>
            <Button type="button" size="sm" onClick={handleSubmit} disabled={uploading}>
              {uploading ? tr.uploadingLabel : tr.submit}
            </Button>
          </div>
        )}

        {state === "stopped" && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            ref={audioRef}
            src={previewUrl}
            className="hidden"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => setPlaybackTime(e.currentTarget.currentTime)}
          />
        )}
      </div>
    </div>
  );
}

export function RecordDialog({ open, onOpenChange, onUpload, uploading, upload }) {
  const { language } = useLanguage();
  const tr = translations[language].recordDialog;
  const [submitting, setSubmitting] = useState(false);
  const busy = upload?.busy ?? uploading;
  const inProgress = busy || submitting;

  async function submit(blob, source, customName) {
    setSubmitting(true);
    try {
      await onUpload(blob, source, customName);
    } catch {
      // onUpload owns user-facing failure feedback.
    } finally {
      onOpenChange(false);
      setSubmitting(false);
    }
  }

  async function cancelAndClose() {
    await upload?.cancel?.();
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && inProgress) {
          void cancelAndClose();
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={!inProgress}
        onEscapeKeyDown={(event) => inProgress && event.preventDefault()}
        onPointerDownOutside={(event) => inProgress && event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{inProgress ? tr.uploadingTitle : tr.recordAudioTitle}</DialogTitle>
        </DialogHeader>
        {inProgress ? (
          <div
            className={cn(
              "flex items-center justify-center rounded-lg border border-dashed border-white/15 px-6 py-6",
              PANEL_HEIGHT
            )}
          >
            <UploadProgress upload={upload} onCancel={cancelAndClose} />
          </div>
        ) : (
          <RecordPanel onSubmit={submit} uploading={inProgress} />
        )}
      </DialogContent>
    </Dialog>
  );
}
